import { put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import superFetch from 'library/helpers/superFetch';
import { billingActions } from './slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { Notification } from 'zComponents/atoms';
import { planLabel, moduleLabel } from 'config/billingCatalogLabels';
import { formatDateAU } from 'library/helpers/formatBilling';
import { customerBillingErrorMessage } from 'library/helpers/billingRefusal';

// Sentence-interpolated, so the empty case must be falsy (null), not an em-dash.
const fmtDate = (d) => formatDateAU(d, null);
const isOk = (statusCode) => statusCode === 200 || statusCode === 201;
const selectOrgID = (state) => state.Auth?.selectedProfile?.orgID;

// Billing reads the org-scoped /billing surface (defaults to /v5 via SuperFetch). State loads on
// boot / org (re)select so entitlement gating is available everywhere (switcher, sidebar, routes).

export function* loadBillingState() {
	let orgID = null;
	try {
		// Triggered on settingsActions.loadOrgSettings, which the Loading-screen retry can
		// dispatch before a workspace is in scope. Without an orgID SuperFetch would call
		// /v5/undefined/billing (404); skip until an org is selected — gating already fails
		// OPEN so nothing is lost by waiting for the org (re)select re-trigger.
		orgID = yield select(selectOrgID);
		if (!orgID) {
			yield put(billingActions.loadBillingStateError('No orgID in current profile'));
			return;
		}
		const res = yield superFetch.get(`/billing`, {}, {}, orgID);
		// The org may have switched while the request was in flight (two watchers run this saga, so
		// takeLatest on one does not cancel the other). Never write org A's billing into org B's session.
		const currentOrgID = yield select(selectOrgID);
		if (String(currentOrgID) !== String(orgID)) return;
		if (isOk(res.statusCode)) {
			yield put(billingActions.loadBillingStateSuccess(res.data || null, orgID));
		} else {
			const err = new Error(res.error);
			err.errorCode = res.errorCode || res.error?.errorCode || null;
			throw err;
		}
	} catch (error) {
		// Non-fatal: if billing can't load, gating fails OPEN (grandfathered) so the dashboard
		// never locks out. Don't spam the user — log only.
		// eslint-disable-next-line no-console
		console.warn('[billing] failed to load billing state:', error.message);
		// stamped with the requested org, so the refusal is never read as another org's
		yield put(
			billingActions.loadBillingStateError(
				{ message: error.message, errorCode: error.errorCode || null },
				orgID ?? null
			)
		);
	}
}

/** v1.15: the customer licensing read's 'hidden' answer (flag off for the partner of record, or no relationship). */
const LICENSING_HIDDEN_STATUS = 404;

/**
 * v1.15 customer licensing read: GET /billing/licensing (App Server AMENDMENTS.v1.15). 200 → loaded; 404 → hidden
 * (today's legacy / unknown path); anything else, a refusal or a network error → unavailable ("Not confirmed").
 * Never read as "not licensed" unless loaded. Loaded beside the billing state, on the same triggers.
 */
export function* loadCustomerLicensing() {
	let orgID = null;
	try {
		orgID = yield select(selectOrgID);
		if (!orgID) {
			yield put(billingActions.loadCustomerLicensingUnavailable(null));
			return;
		}
		const res = yield superFetch.get(`/billing/licensing`, {}, {}, orgID);
		// never write org A's licensing into org B's session
		const currentOrgID = yield select(selectOrgID);
		if (String(currentOrgID) !== String(orgID)) return;
		if (res?.statusCode === 200) yield put(billingActions.loadCustomerLicensingSuccess(res.data ?? null, orgID));
		else if (res?.statusCode === LICENSING_HIDDEN_STATUS) yield put(billingActions.loadCustomerLicensingHidden(orgID));
		else yield put(billingActions.loadCustomerLicensingUnavailable(orgID));
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('[billing] failed to load licensing:', error.message);
		yield put(billingActions.loadCustomerLicensingUnavailable(orgID));
	}
}

export function* activateDeal({ payload }) {
	try {
		const res = yield superFetch.post(`/billing/deals`, payload);
		if (isOk(res.statusCode)) {
			yield put(billingActions.activateDealSuccess(res.data));
			Notification('success', res.data?.created ? 'Deal activated.' : 'Deal already active.');
			yield put(billingActions.loadBillingState());
		} else {
			throw new Error(res.error);
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to activate deal.');
		yield put(billingActions.activateDealError(error.message));
	}
}

export function* grantEntitlement({ payload }) {
	try {
		const res = yield superFetch.post(`/billing/entitlements/grant`, payload);
		if (isOk(res.statusCode)) {
			yield put(billingActions.grantEntitlementSuccess());
			Notification('success', 'Entitlement granted.');
			yield put(billingActions.loadBillingState());
		} else {
			throw new Error(res.error);
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to grant entitlement.');
		yield put(billingActions.grantEntitlementError(error.message));
	}
}

export function* revokeEntitlement({ payload }) {
	try {
		const res = yield superFetch.post(`/billing/entitlements/revoke`, payload);
		if (isOk(res.statusCode)) {
			yield put(billingActions.revokeEntitlementSuccess());
			Notification('success', 'Entitlement revoked.');
			yield put(billingActions.loadBillingState());
		} else {
			throw new Error(res.error);
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to revoke entitlement.');
		yield put(billingActions.revokeEntitlementError(error.message));
	}
}

export function* applyLicenseKey({ payload }) {
	try {
		const res = yield superFetch.post(`/billing/license-keys/apply`, { key: payload.key });
		if (isOk(res.statusCode)) {
			yield put(billingActions.applyLicenseKeySuccess());
			const d = res.data || {};
			if (d.applied) {
				// Display labels only — never raw catalog codes.
				const mods = (d.modules && d.modules.length ? d.modules : d.granted || []).map(moduleLabel).join(', ');
				const expiry = fmtDate(d.expiresAt);
				const detail = [mods, expiry ? `Expires ${expiry}` : null].filter(Boolean).join(' · ');
				Notification('success', `${planLabel(d.plan).full} activated`, detail || undefined);
			} else {
				Notification('info', 'This activation key has already been applied.');
			}
			yield put(billingActions.loadBillingState());
		} else {
			throw new Error(res.error);
		}
	} catch (error) {
		Notification('error', error.message || 'Could not apply activation key.');
		yield put(billingActions.applyLicenseKeyError(error.message));
	}
}

export function* previewLicenseKey({ payload }) {
	try {
		const res = yield superFetch.post(`/billing/license-keys/preview`, { key: payload.key });
		if (isOk(res.statusCode)) {
			yield put(billingActions.previewLicenseKeySuccess(res.data || null));
		} else {
			throw new Error(res.error);
		}
	} catch (error) {
		// Surface the rejection (invalid / expired / wrong-org) in the wizard, not as a toast spam.
		yield put(billingActions.previewLicenseKeyError(error.message || 'Could not read that activation key.'));
	}
}

export function* startStripeSubscription({ payload }) {
	try {
		const res = yield superFetch.post(`/billing/stripe/subscription`, payload);
		if (isOk(res.statusCode)) {
			const data = res.data || {};
			yield put(billingActions.startStripeSubscriptionSuccess(data));
			// A plan change with nothing due now (e.g. a downgrade/credit) returns no clientSecret and so
			// has no card step — confirm it and refresh. When a clientSecret IS present the Payment
			// Element mounts to collect the (prorated) charge, so we stay quiet here.
			if (!data.clientSecret) {
				Notification('success', data.changed ? 'Your plan has been updated.' : 'Subscription started.');
				yield put(billingActions.loadBillingState());
			}
		} else {
			throw new Error(res.error);
		}
	} catch (error) {
		Notification('error', error.message || 'Could not start the subscription.');
		yield put(billingActions.startStripeSubscriptionError(error.message));
	}
}

// Site-trial customer actions (B20, App Server PR 7204): POST /billing/site-trial/agreement/{cancel|resume|card-update}.
// The server refuses with a published errorCode; the words come from library/helpers/billingRefusal.
const SITE_TRIAL_ACTIONS = ['cancel', 'resume', 'card-update'];

const siteTrialSuccessCopy = (action, res) => {
	const d = res.data || {};
	if (action === 'card-update') {
		if (d.route === 'PORTAL' && d.url) return { type: 'success', title: 'Opening the card page.' };
		return {
			type: 'success',
			title: 'Card link sent.',
			description: "It has gone to the organisation's billing contact."
		};
	}
	const queued = res.statusCode === 202 || d.outcome === 'QUEUED';
	const verb = action === 'cancel' ? 'Cancellation' : 'Resume';
	if (d.outcome === 'NETTED') return { type: 'success', title: 'The earlier request has been withdrawn.' };
	if (queued)
		return {
			type: 'info',
			title: `${verb} recorded.`,
			description: 'It will be applied shortly, and takes effect at the end of the current period.'
		};
	return {
		type: 'success',
		title:
			action === 'cancel' ? 'Cancellation scheduled for the end of the period.' : 'Your agreement will continue.'
	};
};

export function* requestSiteTrialAction({ payload }) {
	const { action, operationID } = payload || {};
	try {
		if (!SITE_TRIAL_ACTIONS.includes(action)) throw new Error('Unknown billing action.');
		const res = yield superFetch.post(`/billing/site-trial/agreement/${action}`, { operationID });
		if (res.statusCode === 200 || res.statusCode === 202) {
			yield put(billingActions.requestSiteTrialActionSuccess(res.data || null));
			const copy = siteTrialSuccessCopy(action, res);
			Notification(copy.type, copy.title, copy.description);
			if (action === 'card-update' && res.data?.route === 'PORTAL' && res.data?.url) {
				window.location.assign(res.data.url);
				return;
			}
			yield put(billingActions.loadBillingState());
		} else {
			const message = customerBillingErrorMessage(res);
			Notification('error', message);
			yield put(billingActions.requestSiteTrialActionError({ errorCode: res.errorCode || null, message }));
		}
	} catch (error) {
		Notification('error', error.message || 'That billing change failed.');
		yield put(billingActions.requestSiteTrialActionError({ errorCode: null, message: error.message }));
	}
}

export default function* billingSagas() {
	yield takeLatest(billingActions.loadBillingState, loadBillingState);
	// Load billing whenever org settings (re)load — i.e. on boot and on org switch.
	yield takeLatest(settingsActions.loadOrgSettings, loadBillingState);
	// v1.15: the customer licensing read rides the same triggers (its own watchers, so neither cancels the other).
	yield takeLatest(billingActions.loadBillingState, loadCustomerLicensing);
	yield takeLatest(settingsActions.loadOrgSettings, loadCustomerLicensing);
	yield takeLatest(billingActions.activateDeal, activateDeal);
	yield takeLatest(billingActions.grantEntitlement, grantEntitlement);
	yield takeLatest(billingActions.revokeEntitlement, revokeEntitlement);
	yield takeLatest(billingActions.applyLicenseKey, applyLicenseKey);
	yield takeLatest(billingActions.previewLicenseKey, previewLicenseKey);
	yield takeLatest(billingActions.startStripeSubscription, startStripeSubscription);
	// takeLeading: a second click while one change is in flight is dropped, never sent as a second request.
	yield takeLeading(billingActions.requestSiteTrialAction, requestSiteTrialAction);
}
